import { HostBinding, Injectable, OnDestroy, inject } from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { Theme } from "../enums/Theme";
import { MitpManagerService } from "./mitp-manager.service";
import { OverlayContainer } from "@angular/cdk/overlay";
import { UserService } from "./user.service";
import { Subject, distinctUntilChanged, takeUntil } from "rxjs";

@Injectable({
	providedIn: "root",
})
export class ThemeService implements OnDestroy {
	private manager = inject(MitpManagerService);
	private overlay = inject(OverlayContainer);
	private userService = inject(UserService);
	private destroy$ = new Subject<void>();
	private mediaQueryList =
		typeof window !== "undefined" && typeof window.matchMedia === "function"
			? window.matchMedia("(prefers-color-scheme: dark)")
			: null;
	private mediaQueryHandler = (event: MediaQueryListEvent) => {
		this.themeControl.setValue(event.matches);
	};

	@HostBinding("class") className = "";
	themeControl = new UntypedFormControl(false);

	/*
	 * Check the user's theme preference and set the theme accordingly.
	 * toogleControl = false -> light theme
	 * toogleControl = true -> dark theme
	 */
	checkUserTheme(): void {
		this.userService
			.getUserConfig()
			.pipe(takeUntil(this.destroy$))
			.subscribe((response) => {
				this.manager.setUserConfig(response);
				if (response.theme == Theme.LIGHT) {
					this.themeControl.patchValue(false);
				} else if (response.theme == Theme.DARK) {
					this.themeControl.patchValue(true);
				} else if (response.theme == Theme.SYSTEM) {
					this.themeControl.patchValue(this.mediaQueryList?.matches ?? false);
				}
			});
	}

	setTheme(): void {
		this.themeControl.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((darkMode) => {
			const darkClassName = "darkTheme";
			this.className = darkMode ? darkClassName : "";
			if (darkMode) {
				this.overlay.getContainerElement().classList.add(darkClassName);
			} else {
				this.overlay.getContainerElement().classList.remove(darkClassName);
			}
		});
		if (this.mediaQueryList?.matches) {
			this.themeControl.setValue(true);
		}
		this.mediaQueryList?.addEventListener("change", this.mediaQueryHandler);
	}

	isDarkTheme(): boolean {
		return this.themeControl.value;
	}

	setDarkTheme(): void {
		this.themeControl.setValue(true);
	}

	setLightTheme(): void {
		this.themeControl.setValue(false);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.mediaQueryList?.removeEventListener("change", this.mediaQueryHandler);
	}
}
